<template>
  <div
      class="crop-task-view"
      data-annotate-type="crop"
      :data-groupby="isGrouped ? 'true' : 'false'"
      :data-groupby-order="groupByOrder"
      :data-preloaded-label="preloadedLabelNamesAttr"
      :data-preloaded-count="preloadedLabelCount"
      :data-preloaded-crop-count="preloadedCropCount"
  >
    <row v-if="done">
      <col6>
        <v-card class="elevation-4">
          <v-card-title>{{$t('annotate.no_ann_title')}}</v-card-title>
          <v-card-subtitle>{{$t('annotate.crop_task_done') || 'No more crops for this task.'}}</v-card-subtitle>
        </v-card>
      </col6>
    </row>

    <template v-else-if="isGrouped">
      <row class="mb-3">
        <col8>
          <div class="crop-task-view__heading" data-testid="crop-task-groupby-heading">
            {{ currentLabelName }}
            <span v-if="currentLabelCount" class="text-medium-emphasis">
              ({{ currentLabelCount }} {{ currentLabelCount === 1 ? 'annotation' : 'annotations' }})
            </span>
          </div>
        </col8>
        <col4 class="text-right">
          <v-btn
              v-if="labelIndex < groupedLabels.length - 1"
              variant="outlined"
              size="small"
              id="crop-task-next-label"
              @click="advanceLabel"
          >
            {{$t('annotate.crop_task_next_label') || 'Next label'}}
          </v-btn>
        </col4>
      </row>
      <label-crops-panel
          v-if="currentLabelName"
          :key="currentLabelName + '-' + panelKey"
          :label_name="currentLabelName"
          :filters="panelFilters"
          :total_count="currentLabelCount"
          :preloaded-images="preloadedForCurrent"
          :show-actions="true"
          @empty="onCurrentLabelEmpty"
          @validated="onValidated"
          @loaded="onPanelLoaded"
      ></label-crops-panel>
    </template>

    <template v-else>
      <loader :loading="loading"></loader>
      <div class="crop-task-view__heading mb-3" data-testid="crop-task-grid-heading">
        {{$t('annotate.crop_task_title') || 'Validate crops'}}
      </div>
      <div class="label-crops-panel__actions mb-3">
        <v-btn
            v-if="images.length > 0"
            id="relabel-incorrect"
            @click="relabelSelectedAsIncorrect"
            :disabled="loading || selectedAnnotationIds.length === 0"
            color="error"
            variant="outlined"
            class="mr-2">
          {{$t('myboxes.btn_incorrect') || 'Incorrect'}}
        </v-btn>
        <v-btn
            v-if="images.length > 0"
            @click="validateSelectedCrops"
            :disabled="loading || selectedAnnotationIds.length === 0"
            color="primary">
          {{$t('myboxes.validate_selected') || 'Validate Selected'}}
        </v-btn>
      </div>
      <grid
          ref="grid"
          @reload="loadFlat"
          :items="images"
          :no_toggle="false"
          @update_selection_annotation_ids="updateSelectedAnnotations"
      ></grid>
    </template>
  </div>
</template>

<script>
import LabelCropsPanel from "@/components/LabelCropsPanel";
import Grid from "@/components/Grid";
import Loader from "@/components/layout/loader";
import Row from "@/components/layout/Row";
import Col4 from "@/components/layout/Col4";
import Col6 from "@/components/layout/Col6";
import Col8 from "@/components/layout/Col8";
import {
  CROP_TASK_LABEL_PRELOAD_COUNT,
  CROP_TASK_PRELOAD_COUNT,
  GROUPED_LABELS_COLLECT_LIMIT,
  ensureGroupByLabelFilter,
  filtersHaveGroupBy,
  groupByOrderFromFilters,
  parseTaskFilters,
  preloadCropItems,
} from "@/utils/cropTask";

const INCORRECT_LABEL = 'Incorrect';

export default {
  name: "CropTaskView",
  components: {LabelCropsPanel, Grid, Loader, Row, Col4, Col6, Col8},
  props: {
    filters: {required: true},
  },
  emits: ['update_progress'],
  data() {
    return {
      loading: false,
      done: false,
      isGrouped: false,
      groupedLabels: [],
      labelIndex: 0,
      panelKey: 0,
      panelFilters: [],
      images: [],
      selectedAnnotationIds: [],
      /** @type {Record<string, Array>} label name → preloaded crop grid items */
      preloadedByLabel: {},
      preloadGeneration: 0,
    };
  },
  computed: {
    groupByOrder() {
      return groupByOrderFromFilters(this.panelFilters);
    },
    currentLabel() {
      return this.groupedLabels[this.labelIndex] || null;
    },
    currentLabelName() {
      return this.currentLabel ? this.currentLabel.label : '';
    },
    currentLabelCount() {
      return this.currentLabel ? this.currentLabel.count : 0;
    },
    preloadedForCurrent() {
      const items = this.preloadedByLabel[this.currentLabelName];
      if (Array.isArray(items) && items.length > 0) {
        return items;
      }
      return null;
    },
    /** Upcoming label names in the preload cache (excludes the label currently shown). */
    upcomingPreloadedLabelNames() {
      return Object.keys(this.preloadedByLabel).filter(
        (name) => name && name !== this.currentLabelName && name !== '__flat__',
      );
    },
    preloadedLabelCount() {
      return this.upcomingPreloadedLabelNames.length;
    },
    preloadedCropCount() {
      return this.upcomingPreloadedLabelNames.reduce((sum, name) => {
        const items = this.preloadedByLabel[name];
        return sum + (Array.isArray(items) ? items.length : 0);
      }, 0);
    },
    preloadedLabelNamesAttr() {
      const names = this.upcomingPreloadedLabelNames;
      return names.length ? names.join(',') : null;
    },
  },
  watch: {
    filters: {
      immediate: true,
      handler() {
        this.initiate();
      },
    },
  },
  methods: {
    updateSelectedAnnotations(ids) {
      this.selectedAnnotationIds = ids || [];
    },
    clearPreloadCache() {
      this.preloadedByLabel = {};
      this.preloadGeneration += 1;
    },
    async initiate() {
      this.done = false;
      this.loading = true;
      this.labelIndex = 0;
      this.groupedLabels = [];
      this.images = [];
      this.clearPreloadCache();

      const parsed = parseTaskFilters(this.filters);
      this.isGrouped = filtersHaveGroupBy(parsed);
      this.panelFilters = this.isGrouped ? ensureGroupByLabelFilter(parsed) : parsed;

      if (this.isGrouped) {
        await this.loadGroupedLabels();
      } else {
        await this.loadFlat();
      }
      this.loading = false;
    },
    async loadGroupedLabels() {
      // labels_only + high limit: collect every matching label group so
      // progress ("N left") is the real remaining count, not capped at 100.
      const results = await this.$api.annotation.filter(
        this.panelFilters,
        GROUPED_LABELS_COLLECT_LIMIT,
        { labels_only: true },
      );
      const items = (results && results.items) || [];
      this.groupedLabels = items
        .filter((item) => item && item.label)
        .map((item) => ({
          label: item.label,
          count: item.count || 0,
        }));

      if (this.groupedLabels.length === 0) {
        this.done = true;
        this.$emit('update_progress', 0);
        return;
      }

      const total =
        typeof results.max_results === 'number' && results.max_results > 0
          ? results.max_results
          : this.groupedLabels.length;
      this.$emit('update_progress', total);
      this.panelKey += 1;
      this.warmUpcomingLabels();
    },
    async loadFlat() {
      this.loading = true;
      this.images = [];
      const filters = parseTaskFilters(this.filters);
      const results = await this.$api.annotation.filter(filters, 40);
      const items = (results && results.items) || [];
      if (items.length === 0) {
        this.done = true;
        this.$emit('update_progress', 0);
        this.loading = false;
        return;
      }
      this.images = await preloadCropItems(
        items,
        (url, bbox, assetId) => this.$api.asset.get_crop(url, bbox, assetId),
        items.length,
      );
      // Warm next page of crop bytes for subsequent reloads (same filter window).
      this.preloadGeneration += 1;
      const generation = this.preloadGeneration;
      preloadCropItems(
        items.slice(0, CROP_TASK_PRELOAD_COUNT),
        (url, bbox, assetId) => this.$api.asset.get_crop(url, bbox, assetId),
        CROP_TASK_PRELOAD_COUNT,
      ).then((preloaded) => {
        if (generation === this.preloadGeneration) {
          this.preloadedByLabel = { __flat__: preloaded };
        }
      });
      this.$emit('update_progress', this.images.length);
      this.loading = false;
      this.$nextTick(() => {
        if (this.$refs.grid && this.images.length > 0) {
          this.$refs.grid.toggleSelectAll(true);
        }
      });
    },
    /**
     * Preload the next CROP_TASK_LABEL_PRELOAD_COUNT labels (and all their crops,
     * capped at 40) so switching labels is as snappy as asset annotate preload.
     */
    async warmUpcomingLabels() {
      const generation = this.preloadGeneration + 1;
      this.preloadGeneration = generation;
      const start = this.labelIndex + 1;
      const end = Math.min(
        this.groupedLabels.length,
        start + CROP_TASK_LABEL_PRELOAD_COUNT,
      );

      // Keep the current label's cache (panel may still be consuming it) plus upcoming.
      const keep = new Set();
      if (this.currentLabelName) {
        keep.add(this.currentLabelName);
      }
      for (let i = start; i < end; i += 1) {
        keep.add(this.groupedLabels[i].label);
      }
      const nextCache = {};
      Object.keys(this.preloadedByLabel).forEach((name) => {
        if (keep.has(name)) {
          nextCache[name] = this.preloadedByLabel[name];
        }
      });
      this.preloadedByLabel = nextCache;

      for (let i = start; i < end; i += 1) {
        if (generation !== this.preloadGeneration) {
          return;
        }
        const next = this.groupedLabels[i];
        if (!next || this.preloadedByLabel[next.label]) {
          continue;
        }
        try {
          const results = await this.$api.annotation.filter_by_label(
            this.panelFilters,
            next.label,
          );
          let items = (results && results.items) || [];
          if (items.length > 40) {
            items = items.slice(0, 40);
          }
          const preloaded = await preloadCropItems(
            items,
            (url, bbox, assetId) => this.$api.asset.get_crop(url, bbox, assetId),
            items.length,
          );
          if (generation !== this.preloadGeneration) {
            return;
          }
          this.preloadedByLabel = {
            ...this.preloadedByLabel,
            [next.label]: preloaded,
          };
        } catch (e) {
          // best-effort preload
        }
      }
    },
    advanceLabel() {
      if (this.labelIndex >= this.groupedLabels.length - 1) {
        this.done = true;
        this.$emit('update_progress', 0);
        return;
      }
      const previousLabel = this.currentLabelName;
      this.labelIndex += 1;
      this.panelKey += 1;
      this.$emit('update_progress', this.groupedLabels.length - this.labelIndex);
      // Drop the label we just left; keep the new current (already warmed) for the panel.
      if (previousLabel && this.preloadedByLabel[previousLabel]) {
        const rest = { ...this.preloadedByLabel };
        delete rest[previousLabel];
        this.preloadedByLabel = rest;
      }
      this.warmUpcomingLabels();
    },
    onCurrentLabelEmpty() {
      this.advanceLabel();
    },
    onValidated() {
      // After Validate Selected, move on to the next label group.
      this.advanceLabel();
    },
    onPanelLoaded() {
      // no-op; preload already warming next labels
    },
    async validateSelectedCrops() {
      if (this.selectedAnnotationIds.length === 0) {
        return;
      }
      this.loading = true;
      await this.$api.box.mark_bboxes_validated(this.selectedAnnotationIds);
      await this.loadFlat();
    },
    async relabelSelectedAsIncorrect() {
      if (this.selectedAnnotationIds.length === 0) {
        return;
      }
      this.loading = true;
      try {
        await this.$api.annotation.re_label(
          INCORRECT_LABEL,
          JSON.stringify(this.selectedAnnotationIds),
        );
        await this.loadFlat();
      } catch (error) {
        console.error('Failed to relabel annotations as Incorrect', error);
        this.loading = false;
      }
    },
  },
}
</script>

<style scoped>
.crop-task-view__heading {
  font-size: 1.25rem;
  font-weight: 600;
}
.label-crops-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
